/**
 * The project window: the open project (name, file, saved or not), Save / Save as, a new
 * project, the projects in the projects folder, the recent ones, and opening any file by path.
 *
 * Anything that would replace unsaved work (opening another project, starting a new one) asks
 * for a second click first.
 */
import { For, Show, onMount, type JSX } from "solid-js";
import { useApp } from "../../app/context";
import { createArming } from "../../shared/arming";
import { formatDate } from "../../shared/ui";

/** The project window. */
export function ProjectWindow(): JSX.Element {
  const { project, graph } = useApp();
  const arming = createArming();
  const armed = arming.armed;
  let nameInput!: HTMLInputElement;
  let saveAsInput!: HTMLInputElement;
  let newInput!: HTMLInputElement;
  let pathInput!: HTMLInputElement;

  onMount(() => void project.refresh());

  /** Runs `action` at once, or (with unsaved changes) on the second click of the same `key`. */
  const guarded = (key: string, action: () => void) => arming.confirm(key, action, project.dirty());
  /** Shown under a button waiting for its second click. */
  const warning = "Unsaved changes will be lost. Click again to confirm.";
  /** The last part of a path. */
  const fileName = (path: string) => path.split(/[\\/]/).pop() ?? path;
  /** The open project's file, if it has one. */
  const current = () => project.state()?.path ?? null;

  return (
    <div class="projectwin">
      <Show when={project.state()} fallback={<p class="muted">Waiting for the core…</p>}>
        {(state) => (
          <section class="grp">
            <h3>Open project</h3>
            <label class="fl">
              Name
              <input class="field" id="project-name" ref={nameInput} value={state().name} maxlength="80" />
            </label>
            <p class="mono small" data-project-path>
              {state().path ?? "not saved yet"}
            </p>
            <p class="small" data-project-status={project.dirty() ? "unsaved" : "saved"}>
              <Show when={project.dirty()} fallback={<span class="ok">All changes saved</span>}>
                <span class="warn">
                  Unsaved changes{graph.state.revision !== state().saved_revision ? "" : " (nodes moved)"}
                </span>
              </Show>
            </p>
            <div class="btnrow">
              <button type="button" class="btn primary" id="project-save" title="Save (Ctrl+S)" onClick={() => void project.save(nameInput.value)}>
                Save
              </button>
            </div>
            <div class="row-inline">
              <input class="field" id="saveas-name" ref={saveAsInput} placeholder="Save as: a name or a full path" />
              <button
                type="button"
                class="btn"
                id="project-saveas"
                onClick={() => {
                  const target = saveAsInput.value.trim();
                  if (!target) return saveAsInput.focus();
                  const name = target.includes("/") || target.includes("\\") ? nameInput.value : target;
                  void project.saveAs(target, name).then((ok) => ok && (saveAsInput.value = ""));
                }}
              >
                Save as
              </button>
            </div>
          </section>
        )}
      </Show>

      <section class="grp">
        <h3>New project</h3>
        <div class="row-inline">
          <input class="field" id="new-project-name" ref={newInput} placeholder="Name (optional)" />
          <button
            type="button"
            class="btn"
            classList={{ danger: armed() === "new", armed: armed() === "new" }}
            id="project-new"
            onClick={() => guarded("new", () => void project.create(newInput.value).then((ok) => ok && (newInput.value = "")))}
          >
            {armed() === "new" ? "Confirm" : "New"}
          </button>
        </div>
        <Show when={armed() === "new"}>
          <p class="small warn">{warning}</p>
        </Show>
      </section>

      <section class="grp">
        <h3>
          Projects <span>{project.listing()?.files.length ?? 0}</span>
        </h3>
        <p class="mono small muted">{project.listing()?.folder ?? "no projects folder (--no-files)"}</p>
        <ul class="plist">
          <For each={project.listing()?.files ?? []} fallback={<li class="muted small">No project saved yet.</li>}>
            {(file) => (
              <li classList={{ current: file.path === current() }}>
                <button
                  type="button"
                  class="ref"
                  data-project-file={fileName(file.path)}
                  classList={{ armed: armed() === file.path }}
                  onClick={() => guarded(file.path, () => void project.open(file.path))}
                >
                  <span>{armed() === file.path ? "Click again to open" : (file.name ?? fileName(file.path))}</span>
                  <em>{file.name === null ? "unreadable" : `${file.nodes} nodes · ${formatDate(file.modified)}`}</em>
                </button>
              </li>
            )}
          </For>
        </ul>
        <Show when={armed() && armed() !== "new"}>
          <p class="small warn">{warning}</p>
        </Show>
      </section>

      <Show when={project.listing()?.recent.length}>
        <section class="grp">
          <h3>Recent</h3>
          <ul class="plist">
            <For each={project.listing()?.recent ?? []}>
              {(recent) => (
                <li classList={{ current: recent.path === current() }}>
                  <button
                    type="button"
                    class="ref"
                    disabled={!recent.exists}
                    data-recent={fileName(recent.path)}
                    title={recent.path}
                    onClick={() => guarded(recent.path, () => void project.open(recent.path))}
                  >
                    <span>{armed() === recent.path ? "Click again to open" : recent.name}</span>
                    <em>{recent.exists ? fileName(recent.path) : "missing"}</em>
                  </button>
                  <button type="button" class="x" aria-label={`Forget ${recent.name}`} title="Remove from the list" onClick={() => void project.forget(recent.path)}>
                    ×
                  </button>
                </li>
              )}
            </For>
          </ul>
        </section>
      </Show>

      <section class="grp">
        <h3>Open a file</h3>
        <div class="row-inline">
          <input class="field mono" id="open-path" ref={pathInput} placeholder="/path/to/project.devapp.json" />
          <button type="button" class="btn" id="project-open-path" onClick={() => pathInput.value.trim() && guarded("path", () => void project.open(pathInput.value.trim()))}>
            {armed() === "path" ? "Confirm" : "Open"}
          </button>
        </div>
      </section>
    </div>
  );
}
